import { BackspaceOutlined, FileDownloadOutlined } from "@mui/icons-material";
import { Tab, Tabs, TextField } from "@mui/material";
import clsx from "clsx";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { usePageContext } from "@ryde/redux";
import {
  ActionsEnum,
  AiAdvisorDetailsTab,
  useHasPermissionsCheck,
} from "@ryde/shared";

import { LayoutRight } from "../../../right/layout.right";
import { LayoutRightCrudContentStatic } from "../../../right/layout.right.crud.content.static";
import {
  AiConversationDetailsPlain,
  AiConversationDetailsRaw,
  AiConversationDetailsStructure,
  AiConversationDetailsTreeview,
  DetailsDrawerLayer,
  DetailsTabsContentWrap,
  DetailsTabsScrollContainer,
} from "./parts";
import {
  parseRawReasoningToJsonArray,
  triggerDownloadTextFile,
} from "./parts/ai-conversation.utils";

interface IAiConversationDetailsDrawerProps {
  rawReasoning: string;
  onClose: () => void;
}

export const AiConversationDetailsDrawer: FC<
  IAiConversationDetailsDrawerProps
> = ({ rawReasoning = "", onClose }) => {
  const { t } = useTranslation();
  const { entity } = usePageContext();
  const [tab, setTab] = useState<AiAdvisorDetailsTab>(
    AiAdvisorDetailsTab.PlainText,
  );
  const [searchValue, setSearchValue] = useState("");
  const [fullInfoExpandedAll, setFullInfoExpandedAll] = useState(true);
  const { hasPermissionsCheck } = useHasPermissionsCheck();
  const searchTerm = searchValue.trim();
  const canViewDetails = hasPermissionsCheck({
    entity: "system",
    action: ActionsEnum.ViewAiChatDetails,
  });

  const normalizedRawReasoning = rawReasoning ?? "";
  const fullInfoSrc = useMemo(
    () => parseRawReasoningToJsonArray(normalizedRawReasoning),
    [normalizedRawReasoning],
  );

  return (
    <DetailsDrawerLayer>
      <LayoutRight
        headerTitle={t("aiAdvisor.technicalDetails")}
        showFooter={false}
        closeClick={onClose}
        showBackgroundImage={false}
        entity={entity}
        contentNoScroll
      >
        <LayoutRightCrudContentStatic data-details-drawer="true">
          {!canViewDetails ? (
            <DetailsTabsContentWrap>{null}</DetailsTabsContentWrap>
          ) : (
            <DetailsTabsContentWrap>
              <div className="details-tabs-header">
                <div className="details-search-row">
                  <TextField
                    size="small"
                    placeholder={t("general.SEARCH")}
                    value={searchValue}
                    onChange={e => setSearchValue(e.target.value)}
                    variant="outlined"
                    fullWidth
                  />
                  <button
                    type="button"
                    className="details-search-row__btn"
                    onClick={() => setSearchValue("")}
                    aria-label={t("general.CLEAR")}
                    title={t("general.CLEAR")}
                    disabled={!searchValue}
                  >
                    <BackspaceOutlined />
                  </button>
                  <button
                    type="button"
                    className="details-search-row__btn"
                    onClick={() =>
                      triggerDownloadTextFile(
                        normalizedRawReasoning,
                        `ai-advisor-details-${Date.now()}.txt`,
                      )
                    }
                    aria-label={t("general.EXPORT")}
                    title={t("general.EXPORT")}
                    disabled={!normalizedRawReasoning.trim()}
                  >
                    <FileDownloadOutlined />
                  </button>
                </div>

                <Tabs
                  value={tab}
                  onChange={(_, v) => setTab(v)}
                  sx={{ flexShrink: 0 }}
                >
                  <Tab label={t("aiAdvisor.plainText")} />
                  <Tab label={t("aiAdvisor.fullInfo")} />
                  <Tab label={t("aiAdvisor.rawInfo")} />
                  <Tab label={t("aiAdvisor.structure")} />
                </Tabs>
              </div>

              <DetailsTabsScrollContainer>
                <div
                  className={clsx(
                    tab === AiAdvisorDetailsTab.PlainText &&
                      "details-tab-plain",
                    tab === AiAdvisorDetailsTab.FullInfo &&
                      "details-tab-full-info",
                    tab === AiAdvisorDetailsTab.RawInfo && "details-tab-raw",
                    tab === AiAdvisorDetailsTab.Structure &&
                      "details-tab-structure",
                  )}
                >
                  {tab === AiAdvisorDetailsTab.PlainText && (
                    <AiConversationDetailsPlain
                      rawReasoning={normalizedRawReasoning}
                      searchTerm={searchTerm || undefined}
                    />
                  )}

                  {tab === AiAdvisorDetailsTab.FullInfo && (
                    <AiConversationDetailsTreeview
                      fullInfoSrc={fullInfoSrc}
                      searchTerm={searchTerm || undefined}
                      expandedAll={fullInfoExpandedAll}
                      onExpandAll={() => setFullInfoExpandedAll(true)}
                      onCollapseAll={() => setFullInfoExpandedAll(false)}
                    />
                  )}

                  {tab === AiAdvisorDetailsTab.RawInfo && (
                    <AiConversationDetailsRaw
                      rawReasoning={normalizedRawReasoning}
                      searchTerm={searchTerm || undefined}
                    />
                  )}

                  {tab === AiAdvisorDetailsTab.Structure && (
                    <AiConversationDetailsStructure
                      fullInfoSrc={fullInfoSrc}
                      searchTerm={searchTerm || undefined}
                    />
                  )}
                </div>
              </DetailsTabsScrollContainer>
            </DetailsTabsContentWrap>
          )}
        </LayoutRightCrudContentStatic>
      </LayoutRight>
    </DetailsDrawerLayer>
  );
};
